<script lang="ts" setup>
const { getMetaAttributes } = useApiAttributes()
const { t } = useI18n()
const api = useApi()

const { data: response, execute, status } = useAsyncData(
  'doc:locations-search-continents',
  () => api.locations.continents.fetch(),
  { immediate: false },
)

const endpoints: ApiEndpoint[] = [
  {
    method: 'GET',
    path: '/locations/continents',
  },
]

const queryAttributes: ApiAttribute[] = [
  {
    description: t('attributes.page'),
    name: 'page',
    optional: true,
    type: 'number',
  },
  {
    description: t('attributes.search'),
    name: 'search',
    optional: true,
    type: 'string',
  },
  {
    description: t('attributes.size'),
    name: 'size',
    optional: true,
    type: 'number',
  },
]

const responseAttributes: ApiAttribute[] = [
  {
    description: t('response.continents'),
    name: 'continents',
    type: 'array',

    attributes: [
      {
        description: t('response.code'),
        name: 'code',
        parent: 'continent',
        type: 'string',
      },
      {
        description: t('response.latitude'),
        name: 'latitude',
        parent: 'continent',
        type: 'string',
      },
      {
        description: t('response.longitude'),
        name: 'longitude',
        parent: 'continent',
        type: 'string',
      },
      {
        description: t('response.name'),
        name: 'name',
        parent: 'continent',
        type: 'string',
      },
      {
        description: t('response.nameEs'),
        name: 'nameEs',
        parent: 'continent',
        type: 'string',
      },
      {
        description: t('response.nameFr'),
        name: 'nameFr',
        parent: 'continent',
        type: 'string',
      },
    ],
  },
  getMetaAttributes(),
]

async function onVisibilityChange(visibility: boolean) {
  if (visibility && !response.value) {
    await execute()
  }
}
</script>

<template>
  <ApiDoc
    :endpoints="endpoints"
    :loading="status === 'pending'"
    :query-attributes="queryAttributes"
    :response="response?.data"
    :response-attributes="responseAttributes"
    @visibility-change="onVisibilityChange"
  />
</template>

<i18n lang="json">
{
  "en": {
    "attributes": {
      "page": "The page to fetch (default to 1).",
      "search": "An additional search query applied to the continent name.",
      "size": "The number of continents to be returned (between 1 and 20). Default to 10."
    },
    "response": {
      "code": "The continent code.",
      "continents": "The continents that match your conditions.",
      "latitude": "The continent's latitude.",
      "longitude": "The continent's longitude.",
      "name": "The continent's name in english.",
      "nameEs": "The continent's name in spanish.",
      "nameFr": "The continent's name in french."
    }
  },
  "es": {
    "attributes": {
      "page": "La página que se va a buscar (por defecto 1).",
      "search": "Una consulta de búsqueda adicional aplicada al nombre del continente.",
      "size": "La cantidad de continentes que se devolverán (entre 1 y 20). Por defecto 10."
    },
    "response": {
      "code": "El código del continente.",
      "continents": "Los continentes que coinciden con tus condiciones.",
      "latitude": "Latitud del continente.",
      "longitude": "Longitud del continente.",
      "name": "El nombre del continente en inglés.",
      "nameEs": "El nombre del continente en español.",
      "nameFr": "El nombre del continente en francés."
    }
  },
  "fr": {
    "attributes": {
      "page": "La page à récupérer (par défaut 1).",
      "search": "Une recherche additionnelle appliquée au nom du continent.",
      "size": "Le nombre de continents à retourner (entre 1 et 20). Par défaut 10."
    },
    "response": {
      "code": "Le code du continent.",
      "continents": "Les continents qui correspondent à vos conditions.",
      "latitude": "La latitude du continent.",
      "longitude": "La longitude du continent.",
      "name": "Le nom du continent en anglais.",
      "nameEs": "Le nom du continent en espagnol.",
      "nameFr": "Le nom du continent en français."
    }
  }
}
</i18n>
